import {
  AdvancedSearchUXProvider,
  useAdvancedSearchUX,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/contexts/AdvancedSearchUXContext'
import { DocChatAttachmentsProvider } from 'features/knowledgeDocumentViewer/components/advancedSearch/contexts/DocChatAttachmentsContext'
import { DocumentSearchDataProvider } from 'features/knowledgeDocumentViewer/components/advancedSearch/contexts/DocumentSearchDataContext'
import { useDocumentSearchSubmitRefresh } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useDocumentSearchSubmitRefresh'
import { SearchHistoryItem } from 'features/knowledgeDocumentViewer/types'
import React, { useMemo } from 'react'

import { AgenticChatPage } from './AgenticChatPage'
import { UnifiedSearchLanding } from './UnifiedSearchLanding'
import { UnifiedSearchResults } from './UnifiedSearchResults'

interface AdvancedSearchPageProps {
  siteId: string
  tabId?: string
  isActive?: boolean
  searchState: SearchHistoryItem['searchState']
  linkedDocIds?: string[]
  unitId?: string
}

interface AdvancedSearchUXProviderWithRefreshProps {
  children: React.ReactNode
  siteId: string
  tabId?: string
  isActive: boolean
  searchState: SearchHistoryItem['searchState']
  isLinkedDocsMode: boolean
}

function AdvancedSearchUXProviderWithRefresh({
  children,
  siteId,
  tabId,
  isActive,
  searchState,
  isLinkedDocsMode,
}: AdvancedSearchUXProviderWithRefreshProps): React.ReactElement {
  const onSearchSubmitted = useDocumentSearchSubmitRefresh()

  return (
    <AdvancedSearchUXProvider
      siteId={siteId}
      tabId={tabId}
      isActive={isActive}
      searchState={searchState}
      isLinkedDocsMode={isLinkedDocsMode}
      onSearchSubmitted={onSearchSubmitted}>
      {children}
    </AdvancedSearchUXProvider>
  )
}

function AdvancedSearchContent({
  isLinkedDocsMode,
  showResults,
}: {
  isLinkedDocsMode: boolean
  showResults: boolean
}) {
  const { contentFilter, searchMode, searchState } = useAdvancedSearchUX()
  const showAgenticChat =
    !isLinkedDocsMode &&
    contentFilter === 'documents' &&
    searchMode === 'ai' &&
    searchState.browseAll !== true

  if (showAgenticChat) return <AgenticChatPage />
  return showResults ? <UnifiedSearchResults /> : <UnifiedSearchLanding />
}

export function AdvancedSearchPage({
  siteId,
  tabId,
  isActive = true,
  searchState,
  linkedDocIds,
  unitId,
}: AdvancedSearchPageProps) {
  const isLinkedDocsMode = (linkedDocIds?.length ?? 0) > 0 || !!unitId
  const hasQuery = searchState.query.trim().length > 0
  const browseAll = searchState.browseAll === true
  const showResults = isLinkedDocsMode || hasQuery || browseAll

  const linkedDocsSearchState = useMemo(
    () =>
      isLinkedDocsMode
        ? { ...searchState, contentFilter: 'documents' as const }
        : searchState,
    [isLinkedDocsMode, searchState]
  )

  return (
    <DocChatAttachmentsProvider>
      <DocumentSearchDataProvider
        siteId={siteId}
        tabId={tabId}
        searchQuery={searchState.query}
        contentFilter={linkedDocsSearchState.contentFilter}
        searchMode={linkedDocsSearchState.searchMode ?? 'normal'}
        linkedDocIds={linkedDocIds}
        unitId={unitId}
        browseAll={browseAll}
        columnFilters={searchState.filters}
        pagination={searchState.pagination}
        sorting={searchState.sorting}
        docChat={searchState.docChat}>
        <AdvancedSearchUXProviderWithRefresh
          siteId={siteId}
          tabId={tabId}
          isActive={isActive}
          searchState={linkedDocsSearchState}
          isLinkedDocsMode={isLinkedDocsMode}>
          <AdvancedSearchContent
            isLinkedDocsMode={isLinkedDocsMode}
            showResults={showResults}
          />
        </AdvancedSearchUXProviderWithRefresh>
      </DocumentSearchDataProvider>
    </DocChatAttachmentsProvider>
  )
}
